import { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { MonitorSmartphone, Plus } from 'lucide-react'
import { leases as leaseApi, vmMetrics as vmMetricsApi } from '@/api/endpoints'
import type { VmMetrics } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Err } from '@/components/Err'
import { Empty, PageHead, Section } from '@/components/layout/Page'
import { isClosed } from '@/domain/lease'
import { usePoll } from '@/hooks/usePoll'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { cn } from '@/lib/utils'
import { useArrivedNotice } from '../vm/notice'
import { pill } from '../pills'
import { MyVmRow } from './MyVmRow'

const LEASES_POLL_MS = 5000
// 지금 읽기는 값이라 변경 알림으로 대신할 수 없다 — 정해진 주기로 다시 묻고, 탭이 숨으면 `usePoll` 이 멈춘다.
// 백엔드는 PVE 를 묻지 않고 건강 스윕의 기억에서 답하므로 더 자주 물어도 새 값이 없다.
// 스윕 기본 주기(`clusterhealth.DefaultInterval`)에 맞춘다.
const METRICS_POLL_MS = 5000

// 내 VM (B-9). 사람이 받아서 쓰는 VM 의 목록이고, 한 줄이 임대 하나다 — 이름 · CPU · RAM(고리 아래 사용량/총량) ·
// DISK(할당 글자, 루트와 데이터 볼륨의 합) · 네트워크(수신/송신) · 디스크(읽기/쓰기) · 상태.
// 사양은 고리의 총량과 할당이 말한다. 열마다 폭이 있어 남는 폭은 고르게 퍼진다.
// 여기에는 노드·클러스터·VMID 같은 장비의 사정이 없다. 끝난 것은 기본으로 감춘다.
// 태그는 내가 VM 을 부르는 말이라(F-109) 이름 아래에 서고, 제목 오른쪽의 알약이 그 태그로 목록을 좁힌다(팀 화면의 알약과 같은 모양).
// 고른 태그를 든 VM 이 모두 사라지면 그 알약도 사라지므로 거르개도 함께 풀린다.
// 한 줄은 `MyVmRow.tsx` 의 `memo` 줄이라 지금 읽기가 바뀐 줄만 다시 그린다 — 줄에 넘기는 함수는 고정이다.

export default function MyVms() {
  const nav = useNavigate()
  const onOpen = useCallback((leaseId: string) => nav(`/leases/${leaseId}`), [nav])
  // VM 생성 페이지가 돌아오며 남긴 한 줄. 목록은 새로 그려지며 곧바로 읽으므로 방금 만든 VM 이 함께 선다.
  const [notice, setNotice] = useArrivedNotice()
  const leases = usePoll(() => leaseApi.list(), useFallback(LEASES_POLL_MS))
  useRefetchOn((c) => c.kind === 'lease', leases.refresh)
  const metrics = usePoll(() => vmMetricsApi.list(), METRICS_POLL_MS)

  const [tag, setTag] = useState<string | null>(null)
  const live = (leases.data ?? []).filter((l) => !isClosed(l))
  const tags = [...new Set(live.flatMap((l) => l.tags))].sort((a, b) => a.localeCompare(b))
  const active = tag !== null && tags.includes(tag) ? tag : null
  const rows = live.filter((l) => active === null || l.tags.includes(active))
  const byLease = new Map<string, VmMetrics>((metrics.data ?? []).map((m) => [m.lease_id, m]))

  return (
    <>
      <PageHead
        icon={<MonitorSmartphone className="size-5" />}
        title="내 VM"
        afterTitle={
          tags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setTag(null)} aria-pressed={active === null} className={pill(active === null)}>
                전체
              </button>
              {tags.map((x) => (
                <button key={x} onClick={() => setTag(active === x ? null : x)} aria-pressed={active === x} className={pill(active === x)}>
                  {x}
                </button>
              ))}
            </div>
          ) : undefined
        }
        facts={leases.data ? `${leases.data.filter((l) => !isClosed(l)).length}대` : ''}
        right={
          <div className="mt-0.5 flex items-center gap-2">
            <Button asChild className="bg-brand hover:bg-brand-hover">
              <Link to="/leases/new">
                <Plus className="size-3.5" />
                VM 생성
              </Link>
            </Button>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          className={cn(
            'mt-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px]',
            notice.tone === 'ok' ? 'border-ok/35 bg-ok/8 text-ok-ink' : 'border-danger/35 bg-danger/8 text-danger-ink',
          )}
        >
          <span className="flex-1">{notice.text}</span>
          <button onClick={() => setNotice(null)} className="text-muted-foreground hover:text-foreground">
            닫기
          </button>
        </div>
      ) : null}

      <Section title="전체">
        <Err error={leases.error} />
        {rows.length === 0 ? (
          <Empty>
            <MonitorSmartphone className="size-4" /> {leases.loading && !leases.data ? '불러오는 중' : 'VM 없음'}
          </Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            {/* 좁은 화면에서는 줄이 카드가 된다 — 윗줄에 이름과 상태, 그 아래 고리 둘과 디스크 할당, 마지막 줄에 입출력.
                격자는 `Table` 의 기본 흐름을 덮어써야 하므로 행이 아니라 여기에 적는다. */}
            <Table className="[&_tr]:max-sm:grid [&_tr]:max-sm:grid-cols-3 [&_tr]:max-sm:gap-y-2.5">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-44">VM</TableHead>
                  <TableHead className="w-28 text-center">CPU</TableHead>
                  <TableHead className="w-28 text-center">메모리</TableHead>
                  <TableHead className="w-28 text-center">디스크</TableHead>
                  <TableHead className="w-28">네트워크</TableHead>
                  <TableHead className="w-28">디스크</TableHead>
                  <TableHead className="w-28">상태</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((l) => (
                  <MyVmRow key={l.id} lease={l} m={l.state === 'active' ? byLease.get(l.id) : undefined} onOpen={onOpen} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </>
  )
}
